import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, KORT_PADDING, MODAL_BREDDE, fontStr } from '@/config';
import { gameState } from '@/state/store';
import {
  BACKLIST_REVIVAL_DAGER,
  BACKLIST_REVIVAL_PRIS,
} from '@/state/GameState';
import type { ContractedBook } from '@/data/types';
import { animateModalGlide } from '@/ui/modalGlide';
import { lagRundetRekt } from '@/ui/RoundedRect';
import { modalFokusTrap } from '@/ui/ModalFokusTrap';
import { Kolonne, boksHoyde } from '@/ui/layout';

const SHEET_WIDTH = MODAL_BREDDE;
const ROW_WIDTH = MODAL_BREDDE - 60;
// Runde 45: normalisert kort-internt padding (var 24).
const ROW_PADDING = KORT_PADDING;
const RAD_GAP = 16;

export class BokhandelModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private scrollY = 0;
  private scrollMax = 0;

  constructor(private scene: Phaser.Scene) {}

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(120);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.scene.input.on('wheel', this.onWheel, this);
    this.render();
    animateModalGlide(this.scene, this.layer, { ariaTittel: 'Bokhandel' });
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    if (!this.layer) return;
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.scene.input.off('wheel', this.onWheel, this);
    this.scrollY = 0;
    this.layer?.destroy(true);
    this.layer = undefined;
    modalFokusTrap.lukk();
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const snap = gameState.snapshot;
    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 96;
    const sheetH = GAME_HEIGHT - 192;

    const sheet = lagRundetRekt(
      this.scene,
      sheetX,
      sheetY,
      SHEET_WIDTH,
      sheetH,
      COLORS.beige,
      0.98,
      14,
    ).setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const headerY = sheetY + 36;
    // Layout-primitiv (2026-06-04): header, beskrivelse og kapital stables
    // fra målt høyde via Kolonne. Erstatter de hardkodede/halv-dynamiske
    // y-tallene (+76 / beskrivelse.height+16) som sprakk når header eller
    // beskrivelse wrappet til flere linjer enn antatt.
    const header = this.scene.add
      .text(sheetX + ROW_PADDING, 0, 'Bokhandel — Backlist-revival', {
        fontFamily: FONTS.display,
        fontSize: fontStr(32),
        color: '#862736',
        wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
      })
      .setOrigin(0, 0);

    const beskrivelse = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        0,
        'Re-boost en utgitt bok i 14 dager. Bra for bøker som har solgt seg ferdig men kan vekkes til live.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#3a2e22',
          wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
        },
      )
      .setOrigin(0, 0);

    const kapital = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        0,
        `Dag ${snap.dag} · ${snap.kroner.toLocaleString('nb-NO')} kr`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#3a2e22',
          fontStyle: '600',
        },
      )
      .setOrigin(0, 0);

    const topp = new Kolonne(sheetX + ROW_PADDING, headerY, 16);
    topp.legg(header);
    topp.legg(beskrivelse, 12);
    topp.legg(kapital, 16);
    this.layer.add([header, beskrivelse, kapital]);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - ROW_PADDING, headerY - 28, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const listX = sheetX + (SHEET_WIDTH - ROW_WIDTH) / 2;
    const listTop = topp.bunn() + 24;
    const listBunn = sheetY + sheetH - 32;
    const listHoyde = listBunn - listTop;

    const listContainer = this.scene.add.container(0, 0);
    this.layer.add(listContainer);

    const maske = this.scene.make.graphics({ x: 0, y: 0 }, false);
    maske.fillStyle(0xffffff);
    maske.fillRect(listX - 4, listTop, ROW_WIDTH + 8, listHoyde);
    const maskeObj = maske.createGeometryMask();
    listContainer.setMask(maskeObj);

    const boker = this.tilgjengeligeBoker(snap.utgitteBoker, snap.iProduksjon);

    let y = listTop - this.scrollY;
    let bygdHoyde = 0;

    if (boker.length === 0) {
      const tomBoks = lagRundetRekt(
        this.scene,
        listX,
        listTop,
        ROW_WIDTH,
        180,
        COLORS.white,
        0.9,
        8,
      ).setStrokeStyle(2, COLORS.oakDark, 0.4);
      const tomTxt = this.scene.add
        .text(
          listX + ROW_WIDTH / 2,
          listTop + 90,
          'Ingen utgitte bøker ennå.\nUtgi noen bøker først, så kan du re-booste dem her.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(22),
            color: '#1a1a1a',
            align: 'center',
          },
        )
        .setOrigin(0.5);
      this.layer.add([tomBoks, tomTxt]);
    } else {
      for (const bok of boker) {
        const { container, hoyde } = this.byggBokRad(
          listX,
          y,
          bok,
          snap.dag,
          snap.kroner,
        );
        listContainer.add(container);
        y += hoyde + RAD_GAP;
        bygdHoyde += hoyde + RAD_GAP;
      }
    }

    this.scrollMax = Math.max(0, bygdHoyde - listHoyde);
    if (this.scrollY > this.scrollMax) this.scrollY = this.scrollMax;
    this.bindScroll(listX, listTop, listHoyde);
  }

  private bindScroll(listX: number, listTop: number, listHoyde: number): void {
    if (!this.layer) return;
    if (this.scrollMax <= 0) return;
    const sone = this.scene.add
      .rectangle(listX, listTop, ROW_WIDTH, listHoyde, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive();
    let dragStart = 0;
    let scrollStart = 0;
    sone.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragStart = p.y;
      scrollStart = this.scrollY;
    });
    sone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown) return;
      const delta = dragStart - p.y;
      this.scrollY = Math.max(0, Math.min(this.scrollMax, scrollStart + delta));
      this.render();
    });
    this.layer.add(sone);
  }

  private onWheel = (
    _pointer: Phaser.Input.Pointer,
    _objs: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => {
    if (!this.layer || this.scrollMax <= 0) return;
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    this.render();
  };

  private tilgjengeligeBoker(
    utgitte: ReadonlyArray<ContractedBook>,
    iProduksjon: ReadonlyArray<ContractedBook>,
  ): ContractedBook[] {
    const samlet: ContractedBook[] = [];
    for (const b of iProduksjon) if (b.status === 'utgitt') samlet.push(b);
    for (const b of utgitte) samlet.push(b);
    samlet.sort((a, b) => b.salg - a.salg);
    return samlet;
  }

  private byggBokRad(
    x: number,
    y: number,
    bok: ContractedBook,
    dag: number,
    kroner: number,
  ): { container: Phaser.GameObjects.Container; hoyde: number } {
    const c = this.scene.add.container(x, y);

    // Layout-primitiv: tekstene måles og stables med Kolonne, så radhøyden
    // beregnes fra faktisk innhold. Erstatter de hardkodede y=16/70/110 i
    // en fast 160px-rad — som overlappet når tittelen wrappet til 2 linjer.
    const tittel = this.scene.add
      .text(ROW_PADDING, 0, bok.title, {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
        wordWrap: { width: ROW_WIDTH - 320 },
      })
      .setOrigin(0, 0);

    const forfatter = this.scene.add
      .text(ROW_PADDING, 0, bok.author, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#3a2e22',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);

    const dagerSiden =
      bok.utgivelsesDag !== null ? Math.max(0, dag - bok.utgivelsesDag) : 0;
    const meta = this.scene.add
      .text(
        ROW_PADDING,
        0,
        `${bok.salg.toLocaleString('nb-NO')} solgt · utgitt for ${dagerSiden} dager siden`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#3a2e22',
          wordWrap: { width: ROW_WIDTH - 320 },
        },
      )
      .setOrigin(0, 0);

    const innhold = new Kolonne(ROW_PADDING, ROW_PADDING, 8);
    innhold.legg(tittel);
    innhold.legg(forfatter);
    innhold.legg(meta);

    const revival = gameState.backlistRevivalFor(bok.isbn);
    const aktiv = !!revival;
    const knappW = 260;
    const knappH = 56;
    const knappX = ROW_WIDTH - knappW - 16;
    // Radhøyde rommer enten teksten (med padding) eller knappen sentrert
    // med luft — det største av de to. Knappen ligger til høyre, teksten
    // wrapper på ROW_WIDTH-320 så de ikke kolliderer horisontalt.
    const radHoyde = Math.max(
      boksHoyde(innhold.hoyde(), ROW_PADDING),
      knappH + ROW_PADDING * 2,
    );
    const knappY = (radHoyde - knappH) / 2;
    const harRaad = kroner >= BACKLIST_REVIVAL_PRIS;

    const bg = lagRundetRekt(
      this.scene,
      0,
      0,
      ROW_WIDTH,
      radHoyde,
      COLORS.white,
      1,
      8,
    ).setStrokeStyle(2, COLORS.oakDark, 0.35);
    const venstreBorder = lagRundetRekt(this.scene, 0, 0, 4, radHoyde, COLORS.ochre, 1, 2);
    // Bakgrunn + border bakerst, så tekst og knapp ligger oppå.
    c.add(bg);
    c.add(venstreBorder);
    c.add([tittel, forfatter, meta]);

    if (aktiv && revival) {
      const igjen = Math.max(0, revival.ferdigDag - dag);
      const tagBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, COLORS.forest, 1)
        .setOrigin(0, 0);
      const tagTxt = this.scene.add
        .text(
          knappX + knappW / 2,
          knappY + knappH / 2,
          `Boostes nå (${igjen} dager igjen)`,
          {
            fontFamily: FONTS.hud,
            fontSize: fontStr(16),
            color: '#ffffff',
            fontStyle: '700',
            align: 'center',
          },
        )
        .setOrigin(0.5);
      c.add([tagBg, tagTxt]);
    } else {
      const fill = harRaad ? COLORS.wine : 0xcfc6b7;
      const farge = harRaad ? '#ffffff' : '#7a7060';
      const tekst = harRaad
        ? `Boost (${BACKLIST_REVIVAL_PRIS.toLocaleString('nb-NO')} kr)`
        : `For lite (${BACKLIST_REVIVAL_PRIS.toLocaleString('nb-NO')} kr)`;
      const kBg = this.scene.add
        .rectangle(knappX, knappY, knappW, knappH, fill, 1)
        .setOrigin(0, 0);
      const kTxt = this.scene.add
        .text(knappX + knappW / 2, knappY + knappH / 2, tekst, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: farge,
          fontStyle: '700',
        })
        .setOrigin(0.5);
      c.add([kBg, kTxt]);
      if (harRaad) {
        kBg.setInteractive({ useHandCursor: true });
        kBg.on('pointerover', () => kBg.setAlpha(0.85));
        kBg.on('pointerout', () => kBg.setAlpha(1));
        kBg.on('pointerup', () => {
          const ok = gameState.kjoepBacklistRevival(bok.isbn);
          if (ok) this.render();
        });
      }
    }

    void BACKLIST_REVIVAL_DAGER;
    return { container: c, hoyde: radHoyde };
  }
}
